import { useState, useEffect } from "react";
import { FaStarOfLife } from "react-icons/fa6";
import { IoMdClose } from "react-icons/io";
import { FaPaperPlane } from "react-icons/fa";
import { BsSave2 } from "react-icons/bs";
import Button from "../Button";
import InputField from "../InputField";
import { VscGitFetch } from "react-icons/vsc";
import { FormFooter } from "../Shared";

const FormFields = ({
  fields,
  formData,
  handleInputChange,
  onSubmit,
  onClear,
  isEditing,
  onSubmitAndAddNew,
  loading = false,
}) => {
  const [errors, setErrors] = useState({});
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (formData.image_Upload) {
      if (typeof formData.image_Upload === "string") {
        setPreviewUrl(formData.image_Upload);
      } else if (formData.image_Upload instanceof File) {
        const fileUrl = URL.createObjectURL(formData.image_Upload);
        setPreviewUrl(fileUrl);
      }
    } else {
      setPreviewUrl(null);
    }
  }, [formData.image_Upload]);

  const validateFields = () => {
    const newErrors = {};
    fields.forEach((field) => {
      const value = formData[field.name]?.toString().trim() || "";
      if (field.required && !value && field.type !== "file") {
        newErrors[field.name] =
          field.errorMessage || `${field.label} is required!`;
      }
      if (value && field.pattern && !field.pattern.test(value)) {
        newErrors[field.name] =
          field.errorMessage || `Invalid ${field.label} format`;
      }
      if (field.type === "number" && value) {
        if (field.min && Number(value) < field.min) {
          newErrors[field.name] =
            field.errorMessage || `Minimum value is ${field.min}`;
        }
        if (field.max && Number(value) > field.max) {
          newErrors[field.name] =
            field.errorMessage || `Maximum value is ${field.max}`;
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChangeWithValidation = (val, field) => {
    const value =
      val?.target?.type === "file"
        ? val.target.files[0]
        : (val?.target?.value ?? val?.value ?? val);
    const name = val?.target?.name ?? field.name;

    if (field.type === "file" && val?.target?.files?.[0]) {
      setPreviewUrl(URL.createObjectURL(val.target.files[0]));
    }

    handleInputChange({ target: { name, value } });

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateFields()) onSubmit(e);
  };

  const handleSaveAndAddNew = (e) => {
    e.preventDefault();
    if (validateFields()) onSubmitAndAddNew?.(e);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="grid grid-cols-12 gap-x-6 gap-y-4 p-6 overflow-y-auto custom-scrollbar">
        {fields?.map((field) => {
          if (field.type === "section") {
            return (
              <div
                key={field.label}
                className="col-span-12 flex items-center gap-4 mt-4 mb-2"
              >
                <span className="text-slate-800 font-bold tracking-wide border-l-4 border-orange pl-3 text-base uppercase">
                  {field.label}
                </span>
                <div className="flex-1 h-[1px] bg-gray-200"></div>
              </div>
            );
          }
          return (
            <div
              key={field.name}
              className={field.grid || "col-span-12 sm:col-span-6"}
            >
              <label className="block mb-2 text-sm font-medium text-gray-800 tracking-wide capitalize">
                {field.label}
                {field.required && (
                  <FaStarOfLife className="inline text-red-500 ml-1 w-2 h-2" />
                )}
              </label>
              {field.fetchable ? (
                <div className="flex gap-2">
                  <div className="flex-1">
                    <InputField
                      type={field.type}
                      name={field.name}
                      value={formData[field.name]}
                      onChange={(val) =>
                        handleInputChangeWithValidation(val, field)
                      }
                      placeholder={field.placeholder || `Enter ${field.label}`}
                      required={field.required}
                      error={errors[field.name]}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => field.onFetch?.(formData[field.name])}
                    className="px-3 py-2 bg-gray-200 text-primary text-xs rounded-lg transition-all"
                  >
                    {field.actionLabel || <VscGitFetch size={18} />}
                  </button>
                </div>
              ) : (
                <InputField
                  // label={field.label}
                  type={field.type}
                  name={field.name}
                  value={formData[field.name]}
                  onChange={(val) =>
                    handleInputChangeWithValidation(val, field)
                  }
                  placeholder={field.placeholder || `Enter ${field.label}`}
                  error={errors[field.name]}
                  required={field.required}
                  disabled={field.disabled}
                  options={field.options}
                  onAddNewClick={field.onAddNew}
                  previewUrl={field.name === "image_Upload" ? previewUrl : null}
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  accept={field.accept}
                  unit={field.unit}
                />
              )}
            </div>
          );
        })}
      </div>

      <FormFooter
        onCancel={onClear}
        onSubmit={handleSubmit}
        isSaving={loading}
        submitText={isEditing ? "Update" : "Submit"}
      >
        {onSubmitAndAddNew && (
          <button
            type="button"
            onClick={handleSaveAndAddNew}
            disabled={loading}
            className="px-6 py-2 border border-primary text-primary font-black text-[10px] uppercase tracking-[2.5px] hover:bg-blue-50 transition-all"
          >
            {isEditing ? "Update & Add New" : "Save & Add New"}
          </button>
        )}
      </FormFooter>
    </form>
  );
};

export default FormFields;
